import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { Send, Loader2, MessageSquare, Mail, Check, CheckCheck, Reply, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

interface WorkspaceMessage {
  id: string;
  workspace_id: string;
  sender_id: string;
  recipient_id: string | null;
  subject: string | null;
  content: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  sender_profile?: {
    name: string | null;
    email: string;
    avatar_url: string | null;
  } | null;
}

interface MemberOption {
  user_id: string;
  name: string;
  email: string;
}

interface WorkspaceMessagesProps {
  workspaceId: string;
  currentUserRole?: string;
  preselectedRecipientId?: string | null;
}

export function WorkspaceMessages({ workspaceId, currentUserRole, preselectedRecipientId }: WorkspaceMessagesProps) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<WorkspaceMessage[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState('');
  const [recipientId, setRecipientId] = useState<string>(preselectedRecipientId || '_admins');

  const isAdminOrOwner = currentUserRole === 'owner' || currentUserRole === 'admin';

  // Sync preselectedRecipientId when it changes
  useEffect(() => {
    if (preselectedRecipientId) {
      setRecipientId(preselectedRecipientId);
    }
  }, [preselectedRecipientId]);

  const loadMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('workspace_members')
        .select('user_id, profiles:user_id (name, email)')
        .eq('workspace_id', workspaceId)
        .eq('seat_status', 'active');

      if (error) throw error;

      const memberOptions: MemberOption[] = (data || [])
        .filter((m: any) => m.user_id !== user?.id)
        .map((m: any) => ({
          user_id: m.user_id,
          name: m.profiles?.name || m.profiles?.email || 'Unbekannt',
          email: m.profiles?.email || '',
        }));

      setMembers(memberOptions);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  };

  const loadMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('workspace_messages')
        .select(`
          id,
          workspace_id,
          sender_id,
          recipient_id,
          subject,
          content,
          is_read,
          read_at,
          created_at,
          sender_profile:sender_id (
            name,
            email,
            avatar_url
          )
        `)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setMessages((data as any) || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
    loadMessages();
  }, [workspaceId]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (!user || !isAdminOrOwner) return;
    const unreadIds = messages
      .filter(m => !m.is_read && m.sender_id !== user.id)
      .map(m => m.id);
    
    if (unreadIds.length > 0) {
      supabase
        .from('workspace_messages')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .in('id', unreadIds)
        .then(() => {
          setMessages(prev => prev.map(m => 
            unreadIds.includes(m.id) ? { ...m, is_read: true } : m
          ));
        });
    }
  }, [messages, user, isAdminOrOwner]);

  const handleReply = (senderId: string) => {
    setRecipientId(senderId);
    // Focus the textarea
    const textarea = document.querySelector<HTMLTextAreaElement>('[data-message-input]');
    textarea?.focus();
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || !user) return;

    setSending(true);
    try {
      const { error } = await supabase
        .from('workspace_messages')
        .insert({
          workspace_id: workspaceId,
          sender_id: user.id,
          recipient_id: recipientId === '_admins' ? null : recipientId,
          content: content.trim(),
        });

      if (error) throw error;

      const recipientName = recipientId === '_admins'
        ? 'Workspace-Admins'
        : members.find(m => m.user_id === recipientId)?.name || 'Mitglied';

      toast({
        title: 'Nachricht gesendet',
        description: `Deine Nachricht wurde an ${recipientName} gesendet.`,
      });
      setContent('');
      loadMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
      toast({
        title: 'Fehler',
        description: 'Nachricht konnte nicht gesendet werden.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const getRecipientLabel = (msg: WorkspaceMessage) => {
    if (msg.recipient_id === null) return 'An Admins';
    if (msg.recipient_id === user?.id) return 'An dich';
    const member = members.find(m => m.user_id === msg.recipient_id);
    return `An ${member?.name || 'Mitglied'}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Compose message */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            Nachricht senden
          </h3>
        </div>
        <form onSubmit={handleSend} className="space-y-3">
          <Select value={recipientId} onValueChange={setRecipientId}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Empfänger wählen" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border z-50">
              <SelectItem value="_admins">
                <span className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5" />
                  Alle Admins / Owner
                </span>
              </SelectItem>
              {members.map((member) => (
                <SelectItem key={member.user_id} value={member.user_id}>
                  {member.name} ({member.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            data-message-input
            placeholder="Schreibe deine Nachricht..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            className="resize-none text-sm"
          />
          <Button
            type="submit"
            size="sm"
            disabled={sending || !content.trim()}
            className="w-full"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wird gesendet...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Nachricht senden
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Message list */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          Verlauf
          {messages.length > 0 && (
            <Badge variant="secondary" className="text-xs">{messages.length}</Badge>
          )}
        </h3>

        {messages.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Noch keine Nachrichten</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {messages.map((msg) => {
              const profile = msg.sender_profile as any;
              const isMine = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`p-3 rounded-lg border text-sm space-y-1.5 ${
                    isMine
                      ? 'border-primary/20 bg-primary/5'
                      : 'border-border/50 bg-muted/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-xs">
                      {isMine ? 'Du' : profile?.name || profile?.email || 'Unbekannt'}
                    </span>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      {msg.is_read ? (
                        <CheckCheck className="h-3 w-3 text-primary" />
                      ) : (
                        <Check className="h-3 w-3" />
                      )}
                      {formatDistanceToNow(new Date(msg.created_at), {
                        addSuffix: true,
                        locale: de,
                      })}
                    </div>
                  </div>
                  <p className="text-foreground whitespace-pre-wrap">{msg.content}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px] h-4">
                      {getRecipientLabel(msg)}
                    </Badge>
                    {!isMine && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs text-muted-foreground hover:text-foreground px-2"
                        onClick={() => handleReply(msg.sender_id)}
                      >
                        <Reply className="h-3 w-3 mr-1" />
                        Antworten
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
